'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'

interface Comparison {
  id: string
  comparisonType: string
  processingStatus: string
  alignedCount: number | null
  partialCount: number | null
  gapCount: number | null
  createdAt: string
  analysis1: { id: string; pdfFilename: string }
  analysis2: { id: string; pdfFilename: string }
}

const COMPARISON_TYPE_LABELS: Record<string, string> = {
  coi_cgl: 'COI ↔ CGL Policy',
  subcontract_cgl: 'Subcontract ↔ CGL Policy',
  subcontract_coi: 'Subcontract ↔ COI'
}

const getStatusBadge = (status: string) => {
  const styles: Record<string, string> = {
    completed: 'bg-green-100 text-green-800',
    processing: 'bg-yellow-100 text-yellow-800 animate-pulse',
    pending: 'bg-gray-100 text-gray-800',
    failed: 'bg-red-100 text-red-800'
  }
  return styles[status] || 'bg-gray-100 text-gray-800'
}

export default function ComparisonsPage() {
  const [comparisons, setComparisons] = useState<Comparison[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const fetchComparisons = () => {
    fetch('/api/comparisons')
      .then(res => res.json())
      .then(data => {
        if (data.error) throw new Error(data.error)
        setComparisons(data)
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchComparisons()
  }, [])

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this comparison? This cannot be undone.')) return

    setDeletingId(id)
    try {
      const res = await fetch(`/api/comparisons/${id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Failed to delete')
      setComparisons(prev => prev.filter(c => c.id !== id))
    } catch (err) {
      alert('Failed to delete comparison')
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
            <p className="text-white/70">Loading comparisons...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <div className="text-center text-red-400 py-12">
          <p className="text-lg font-semibold mb-2">Error</p>
          <p>{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-xl font-semibold text-white mb-6">Comparisons</h1>

        {comparisons.length === 0 ? (
          <div className="text-center py-12 bg-slate-800/50 rounded-lg border border-white/10">
            <p className="text-white/80 mb-4">No comparisons yet</p>
            <p className="text-sm text-white/50 mb-6">
              To create a comparison, go to History and select 2 documents.
            </p>
            <Link
              href="/analyses?mode=select"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors inline-block"
            >
              Go to History
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {comparisons.map(comparison => (
              <div
                key={comparison.id}
                className="bg-slate-800/50 border border-white/10 rounded-lg hover:border-white/20 transition-colors"
              >
                <Link href={`/comparison/${comparison.id}`} className="block p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Comparison Type */}
                      <p className="font-medium text-white mb-2">
                        {COMPARISON_TYPE_LABELS[comparison.comparisonType] || comparison.comparisonType}
                      </p>

                      {/* Document Names */}
                      <div className="text-sm text-white/70 space-y-1">
                        <p className="truncate">{comparison.analysis1.pdfFilename}</p>
                        <p className="text-white/40 text-xs">↕</p>
                        <p className="truncate">{comparison.analysis2.pdfFilename}</p>
                      </div>

                      {/* Timestamp */}
                      <p className="text-xs text-white/40 mt-2">
                        {new Date(comparison.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      {/* Status Badge */}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(comparison.processingStatus)}`}>
                        {comparison.processingStatus}
                      </span>

                      {/* Summary Counts (if completed) */}
                      {comparison.processingStatus === 'completed' && (
                        <div className="flex gap-2 text-xs">
                          {comparison.alignedCount !== null && comparison.alignedCount > 0 && (
                            <span className="text-green-400">✓{comparison.alignedCount}</span>
                          )}
                          {comparison.partialCount !== null && comparison.partialCount > 0 && (
                            <span className="text-yellow-400">⚠{comparison.partialCount}</span>
                          )}
                          {comparison.gapCount !== null && comparison.gapCount > 0 && (
                            <span className="text-red-400">✕{comparison.gapCount}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>

                {/* Delete Button */}
                <div className="px-4 pb-3 flex justify-end">
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      handleDelete(comparison.id)
                    }}
                    disabled={deletingId === comparison.id}
                    className="text-sm text-red-400 hover:text-red-300 hover:bg-red-500/20 px-3 py-1 rounded-md transition-colors disabled:opacity-50"
                  >
                    {deletingId === comparison.id ? (
                      <span className="flex items-center gap-1">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-400"></div>
                        Deleting...
                      </span>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
